import { Box2, Vector2, Vector3 } from 'three';

import { createCanvasWithContext2d } from '../../../../../../utils/dom/element';
import cameraManager from '../../../../../../viewer/camera-manager';
import { SkyviewOrthographicCamera } from '../../../../../../viewer/cameras/skyview-orthographic-camera';
import { pdfPageDimensions } from '../helpers/types';

export type CadDrawingReportImage = {
  url: string;
  width: number;
  height: number;
  gsd?: number;
  backgroundColor: string;
  highResolutionOrthophotoUrl?: string;
};

export type CroppedImageResult = Omit<CadDrawingReportImage, 'backgroundColor'> & {
  coordinateBoundaries: Box2;
};

export type ImageDimensions = {
  width: number;
  height: number;
};

export type CameraExtents = {
  xmin: number;
  xmax: number;
  ymin: number;
  ymax: number;
};

/**
 * Gets the cropped dimensions for the image assuming a 72dpi A3 Landscape PDF page with default padding.
 */
export function getCroppedDimensions(viewWidth: number, viewHeight: number): ImageDimensions {
  const totalPadding = pdfPageDimensions.a3.landscape72Dpi.defaultPadding * 2;
  const aspectHeight = pdfPageDimensions.a3.landscape72Dpi.height - totalPadding;
  const aspectWidth = pdfPageDimensions.a3.landscape72Dpi.width - totalPadding;

  // Calculate new dimensions to maintain aspect ratio.
  const newWidth = viewWidth;
  const newHeight = viewWidth * (aspectHeight / aspectWidth);

  if (newHeight > viewHeight) {
    return {
      height: viewHeight,
      width: viewHeight * (aspectWidth / aspectHeight),
    };
  }

  return {
    width: newWidth,
    height: newHeight,
  };
}

/**
 * Pure function that calculates GSD (Ground Sample Distance) for an image.
 */
export function calculateGSD(imageWidth: number, viewSize: Vector2 | Vector3): number {
  return viewSize.x / imageWidth;
}

/**
 * Pure function that calculates coordinate boundaries for a cropped image.
 */
export function calculateCoordinateBoundaries(
  imageDimensions: ImageDimensions,
  croppedDimensions: ImageDimensions,
  cameraExtents: CameraExtents,
): Box2 {
  // Calculate the ratio of cropped area to full image
  const cropRatioX = croppedDimensions.width / imageDimensions.width;
  const cropRatioY = croppedDimensions.height / imageDimensions.height;

  // Calculate the center offset of the crop
  const centerOffsetX = (imageDimensions.width - croppedDimensions.width) / 2;
  const centerOffsetY = (imageDimensions.height - croppedDimensions.height) / 2;

  // Calculate the coordinate boundaries for the cropped section
  const fullWidth = cameraExtents.xmax - cameraExtents.xmin;
  const fullHeight = cameraExtents.ymax - cameraExtents.ymin;

  const cropWidth = fullWidth * cropRatioX;
  const cropHeight = fullHeight * cropRatioY;

  const cropCenterOffsetX = (centerOffsetX / imageDimensions.width) * fullWidth;
  const cropCenterOffsetY = (centerOffsetY / imageDimensions.height) * fullHeight;

  const cropMinX = cameraExtents.xmin + cropCenterOffsetX;
  const cropMinY = cameraExtents.ymin + cropCenterOffsetY;
  const cropMaxX = cropMinX + cropWidth;
  const cropMaxY = cropMinY + cropHeight;

  return new Box2(new Vector2(cropMinX, cropMinY), new Vector2(cropMaxX, cropMaxY));
}

/**
 * Pure function that processes image cropping logic.
 * This is the main business logic that can be easily tested.
 */
export function processImageCropping(
  imageDimensions: ImageDimensions,
  activeCamera?: SkyviewOrthographicCamera,
): {
  croppedDimensions: ImageDimensions;
  gsd?: number;
  coordinateBoundaries: Box2;
} {
  const croppedDimensions = getCroppedDimensions(imageDimensions.width, imageDimensions.height);

  let gsd: number | undefined = undefined;
  let coordinateBoundaries: Box2 | undefined = undefined;

  if (activeCamera) {
    const viewSize = activeCamera.getViewSize();
    gsd = calculateGSD(imageDimensions.width, viewSize);

    const cameraExtents = activeCamera.getCameraExtents2d();
    coordinateBoundaries = calculateCoordinateBoundaries(
      imageDimensions,
      croppedDimensions,
      cameraExtents,
    );
  }

  return {
    croppedDimensions,
    gsd,
    coordinateBoundaries: coordinateBoundaries || new Box2(new Vector2(0, 0), new Vector2(0, 0)),
  };
}

/**
 * Creates a cropped image data URL from the original image and cropping parameters.
 */
function createCroppedImageDataUrl(
  image: HTMLImageElement,
  croppedDimensions: ImageDimensions,
): string {
  const sourceX = (image.width - croppedDimensions.width) / 2;
  const sourceY = (image.height - croppedDimensions.height) / 2;

  // Draw the cropped section of the image on a canvas and convert to data URL.
  const { canvas, context } = createCanvasWithContext2d(
    croppedDimensions.width,
    croppedDimensions.height,
  );
  context.drawImage(
    image,
    sourceX,
    sourceY,
    croppedDimensions.width,
    croppedDimensions.height,
    0,
    0,
    croppedDimensions.width,
    croppedDimensions.height,
  );

  return canvas.toDataURL('image/png');
}

/**
 * Main function that handles image loading and cropping.
 * This is a wrapper around the pure logic functions.
 */
export async function getCroppedCadImage(dataUrl: string): Promise<CroppedImageResult> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const imageDimensions: ImageDimensions = {
        width: img.width,
        height: img.height,
      };

      const activeCamera = cameraManager().getActiveCamera();
      const { croppedDimensions, gsd, coordinateBoundaries } = processImageCropping(
        imageDimensions,
        activeCamera instanceof SkyviewOrthographicCamera ? activeCamera : undefined,
      );

      const croppedDataUrl = createCroppedImageDataUrl(img, croppedDimensions);

      resolve({
        url: croppedDataUrl,
        height: croppedDimensions.height,
        width: croppedDimensions.width,
        gsd,
        coordinateBoundaries,
      });
    };
    img.src = dataUrl;
  });
}
